import {
  StyleSheet, View, Text, Pressable, ActivityIndicator, RefreshControl, FlatList,
  NativeEventEmitter,
} from 'react-native';
import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { useDispatch, useSelector } from 'react-redux';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import getNotifications from '../requests/Notifications/GetNotifications';
import NotificationItem from './NotificationsLog/NotificationItem';
import Style from '../constants/Style';
import Colors from '../constants/Colors';
import Button from './Button';
import isMobile from './IsMobile';
import ScreenEmptyState from './ScreenEmptyState';
import NoNotificationsImage from './NotificationsLog/NoNotificationsImage';
import { Notification, getNotificationData } from './NotificationsLog/data';
import markNotificationsRead from '../requests/Notifications/MarkNotificationsRead';
import { updateBadgeCount } from '../redux/selectedBuildingSlice';
import MarkSeenIcon from './24pxIcons/MarkSeenIcon';
import MarkUnseenIcon from './24pxIcons/MarkUnseenIcon';
import { logEvent } from './LogEvent';
import { AppScreen } from '../navigation/LinkingConfiguration';
import PressableStyled from './PressableStyled';
import GetUnreadNotificationsCount from '../requests/Notifications/GetUnreadNotificationsCount';

interface NotificationCardProps {
  hideModal: () => void,
}

export default function NotificationsCard({ hideModal }: NotificationCardProps) {
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [clientId, setClientId] = useState<string>('');
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const navigation = useNavigation();
  const badgeCount: number = useSelector(
    (state: { selectedBuilding: { badgeCount: number } }) => state.selectedBuilding.badgeCount,
  ) ?? 0;
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const dispatch = useDispatch();
  const [refs] = useState({});
  const emitter = isMobile ? null : new NativeEventEmitter();

  const navigateToLog: () => void = () => {
    if (isMobile) {
      navigation
        .navigate('Menu' as any, { data: allNotifications, screen: AppScreen.NOTIFICATIONS_LOG });
    } else navigation.navigate(AppScreen.NOTIFICATIONS_LOG as any);
  };

  const viewNotificationsLog = () => {
    hideModal();
    navigateToLog();
  };

  const loadData = () => {
    setLoading(true);
    getNotifications({ buildingId }).then((res) => {
      if (res?.Notifications) {
        const newData = getNotificationData(res?.Notifications ?? []);
        setAllNotifications(newData);
      }
    }).finally(() => setLoading(false));
    GetUnreadNotificationsCount().then((res) => {
      if (!res) return;
      dispatch(updateBadgeCount(res.count ?? 0));
    });
  };

  useEffect(() => {
    loadData();
    const subscription = Notifications.addNotificationReceivedListener(loadData) as any;
    return () => subscription.remove();
  }, [buildingId]);

  useEffect(() => {
    AsyncStorage.getItem('clientName').then(setClientId);
    if (!isMobile) {
      const unsubscribe = navigation.addListener('blur', hideModal);
      return unsubscribe;
    }
    return undefined;
  }, []);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [buildingId]);

  const action = () => {
    if (isMobile) navigateToLog();
    else markAllAsRead();
  };

  const markAllAsRead: () => void = () => {
    setLoading(true);
    logEvent('NotificationsCard', 'mark_all_read');
    markNotificationsRead().then((res) => {
      if (res) {
        dispatch(updateBadgeCount(0));
        emitter?.emit('mark_all_notifications_read');
        loadData();
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const markRead = (
    item: Notification,
    setItemLoading: Dispatch<SetStateAction<boolean>>,
    setIsRead: Dispatch<SetStateAction<boolean>>,
  ) => {
    setItemLoading(true);
    markNotificationsRead(item.id, !item.isRead).then((res) => {
      if (res) {
        setIsRead(!item.isRead);
        dispatch(updateBadgeCount(Math.max(0, badgeCount + (item.isRead ? 1 : -1))));
        setAllNotifications(allNotifications.map((dataItem: Notification) => (dataItem.id
          === item.id ? { ...item, isRead: !item.isRead } : dataItem)));
        refs[item.id]?.current?.close();
      }
      setItemLoading(false);
    }).catch(() => {
      setItemLoading(false);
    });
  };

  const HiddenContent = ({
    notification, itemLoading, setItemLoading, isRead, setIsRead,
  }) => <Pressable
    style={(state: { pressed: boolean }) => [
      styles.hiddenContent, state.pressed && Style.pressed,
    ]}
    disabled={itemLoading}
    onPress={() => {
      logEvent('NotificationsCard', isRead ? 'mark_unseen' : 'mark_seen');
      markRead(notification, setItemLoading, setIsRead);
    }}
  >
      {itemLoading && <ActivityIndicator size='small' />}
      {!itemLoading && (isRead ? <MarkUnseenIcon /> : <MarkSeenIcon />)}
    </Pressable>;

  const SwipeableItem = ({ notification }) => {
    const [itemLoading, setItemLoading] = useState<boolean>(false);
    const [isRead, setIsRead] = useState<boolean>(notification.isRead);
    return <Swipeable
      ref={(ref) => {
        refs[notification.id] = { current: ref };
      }}
      renderRightActions={() => <HiddenContent {...{
        notification, itemLoading, setItemLoading, isRead, setIsRead,
      }} />}
      onSwipeableOpen={() => markRead(notification, setItemLoading, setIsRead)}
    >
      <ListItem {...{ notification }} />
    </Swipeable>;
  };

  const ListItem = ({ notification }) => <NotificationItem
    {...notification}
    dropdownView
    {...{ clientId }}
    expand={() => expand(notification)}
  />;

  const expand: Function = (notification: Notification) => {
    setAllNotifications(allNotifications.map((dataItem: Notification) => (dataItem.id
      === notification.id ? { ...notification, expanded: !notification.expanded }
      : dataItem)));
  };

  return <GestureHandlerRootView
    style={[styles.container, isMobile ? styles.containerMobile : styles.containerWeb]}
    testID='notification-center-modal'
  >
    {!isMobile && buildingId && <ActivityIndicator
      size='large'
      style={Style.activityIndicator}
      animating={loading}
    />}
    <View style={styles.header}>
      <Text style={styles.title}>{`Notifications (${badgeCount})`}</Text>
      {!!buildingId && (isMobile || badgeCount > 0) && <PressableStyled
        onPress={action}
        testID='mark-all-as-read-btn'
        style={styles.viewLogButton}
      >
        <Text style={styles.markReadText}>
          {isMobile ? 'View notification log' : 'Mark all as read'}
        </Text>
      </PressableStyled>}
    </View>
    {(!!buildingId && notEmpty) && (isMobile ? <View style={{ flex: 1 }}>
      <View style={[Style.row, styles.listHeader]}>
        {badgeCount > 0 && <PressableStyled onPress={markAllAsRead}>
          <Text style={styles.markReadText}>{'Mark all as read'}</Text>
        </PressableStyled>}
      </View>
      <FlatList
        refreshControl={<RefreshControl
          refreshing={loading}
          onRefresh={loadData}
        />}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={allNotifications
          .sort((a: Notification, b: Notification) => b.timestamp.localeCompare(a.timestamp))}
        renderItem={({ item: notification }) => <SwipeableItem {...{ notification }} />}
        ListEmptyComponent={loading ? null : <ScreenEmptyState
          style={styles.emptyState}
          image={<NoNotificationsImage />}
          title='No notifications'
          subtitle='You currently have no notifications. The system will notify you when it updates'
        />}
        keyExtractor={(item: Notification) => `notification_dropdown_read_item_${item.id}`}
      />
    </View> : <>
      <FlatList
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={allNotifications.filter((notification: Notification) => !notification.isRead)
          .sort((a: Notification, b: Notification) => b.timestamp.localeCompare(a.timestamp))}
        renderItem={({ item: notification }) => <NotificationItem
          {...notification}
          dropdownView
          {...{ clientId, hideModal }}
          expand={() => expand(notification)}
        />}
        ListEmptyComponent={loading ? null : <ScreenEmptyState
          style={styles.emptyState}
          image={<NoNotificationsImage />}
          title='No unread notifications'
          subtitle='You currently have no unread notifications. The system will notify you when it updates'
        />}
        keyExtractor={(item: Notification) => `notification_dropdown_unread_item_${item.id}`}
      />
      <Button
        title='View all'
        affirmative
        onPress={viewNotificationsLog}
        style={styles.button}
        testID='view-all-btn'
      />
    </>)}
    {!notEmpty && <ScreenEmptyState image={<NoNotificationsImage />} />}
  </GestureHandlerRootView>;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFF',
  },
  containerMobile: {
    height: '100%',
    paddingTop: 32,
  },
  containerWeb: {
    maxHeight: '50%',
    minHeight: 556,
    width: 450,
    marginEnd: 20,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    paddingBottom: 24,
  },
  header: {
    ...Style.row,
    padding: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue50,
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  viewLogButton: {
    maxWidth: '33%',
  },
  markReadText: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlue500,
  },
  list: {
    marginHorizontal: isMobile ? 8 : 0,
    flex: 1,
    overflow: 'hidden',
  },
  listContent: {
    minHeight: isMobile ? undefined : '80%',
    flexGrow: 1,
    paddingHorizontal: isMobile ? 16 : 24,
    paddingVertical: 16,
    paddingBottom: isMobile ? 20 : undefined,
  },
  button: {
    marginStart: 16,
    marginTop: 16,
    alignSelf: 'flex-start',
    flex: undefined,
  },
  emptyState: {
    paddingTop: isMobile ? 12 : 40,
    paddingBottom: isMobile ? 0 : 40,
  },
  hiddenContent: {
    width: 72,
    height: '100%',
    alignSelf: 'flex-end',
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingEnd: 18,
  },
  listHeader: {
    marginTop: 16,
    paddingBottom: 12,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  listHeaderTitle: {
    fontSize: 16,
    lineHeight: 18,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack600,
  },
});
